import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { STATISTICS_PERMISSIONS } from 'constants/statisticsAccess';

import { List, NavItem } from 'types';
import { CatalogueMenu } from 'types/api/product.type';

import { useAppSelector } from 'redux/hooks';
import { getUserPermissions } from 'redux/slices/userData';

import { LayoutContext } from 'contexts';

import { routes } from 'data/routes';

import { useCatalogueLabels } from './useCatalogueLabels';

const useNavData = (): NavItem[] => {
  const { menu, orderId } = useAppSelector((state) => state.settings);
  const permissions = useAppSelector(getUserPermissions);

  const { t } = useTranslation();

  const { isOrderModalVisibleSet } = useContext(LayoutContext);

  const { genreLabel, categoryLabel } = useCatalogueLabels();

  const orderItems = [];

  if (permissions.includes('INSERT_ORDER')) {
    orderItems.push({
      label: t('order.newOrder'),
      onClick: () => {
        isOrderModalVisibleSet(true);
      },
    });
  }

  if (permissions.includes('VIEW_ORDER')) {
    orderItems.push({
      label: t('nav.viewOrders'),
      to: routes.viewOrders,
    });
  }

  if (orderId) {
    orderItems.splice(1, 0, {
      label: t('order.currentOrder'),
      to: routes.singleOrder(orderId),
    });
  }

  const menuIntoList = menu?.map(
    (item: CatalogueMenu, i: number): List => ({
      id: i,
      title: genreLabel(item.nodeName),
      items: item.nodeName2
        .map((subItem) => ({
          label: categoryLabel(subItem.name),
          order: subItem.order,
          to: `${
            routes.catalogue
          }/${item.nodeName.toLocaleLowerCase()}/${subItem.name.toLocaleLowerCase()}`,
        }))
        .sort((a, b) => a.order - b.order),
    })
  );

  return [
    {
      order: 1,
      label: t('nav.home'),
      to: routes.home,
    },
    {
      order: 2,
      label: t('nav.catalogue'),
      list: menuIntoList,
      emptyMessage: t('catalogue.isEmpty'),
      hidden: !permissions.includes('CATALOGUE'),
    },
    {
      order: 3,
      label: t('nav.orders'),
      hidden: !permissions.includes('VIEW_ORDER') && !permissions.includes('INSERT_ORDER'),
      list: [
        {
          id: 1,
          items: orderItems,
        },
      ],
    },
    {
      order: 4,
      label: t('nav.statistics'),
      to: routes.statistics,
      hidden: !STATISTICS_PERMISSIONS.some((permission) => permissions.includes(permission)),
    },
    {
      order: 5,
      label: t('nav.budgets'),
      to: routes.budgets,
      hidden: !permissions.includes('INSERT_BUDGET'),
    },
    {
      order: 6,
      label: t('nav.notifications'),
      to: routes.notifications,
      hidden: !permissions.includes('NOTIFICATIONS') && !permissions.includes('CATALOGUE'),
    },
    {
      order: 7,
      label: t('nav.selections'),
      list: [
        {
          id: 1,
          items: [
            {
              label: t('selection.myListsPlural'),
              to: routes.myLists,
            },
            // {
            //   label: t('clusters'),
            //   to: '#',
            // },
          ],
        },
      ],
    },
    {
      order: 8,
      label: t('nav.downloads'),
      list: [
        {
          id: 1,
          items: [
            {
              label: t('catalogue.pdf'),
              to: routes.downloads,
            },
          ],
        },
      ],
    },
    {
      order: 9,
      label: t('nav.info'),
      list: [
        {
          id: 1,
          items: [
            {
              label: t('catalogue.technologies'),
              to: routes.technologies,
            },
            {
              label: t('statistics.other'),
              to: routes.others,
            },
          ],
        },
      ],
    },
    // Customer Mapper feature disabled for now — keep the entry commented so it
    // can be restored as-is when the feature comes back.
    // {
    //   order: 10,
    //   label: t('nav.customerMapper'),
    //   // ADMIN manages Customer Mapper through the dedicated admin panel, not
    //   // here — excluded even on a dual-role (ADMIN + CUSTOMER_MAPPER)
    //   // account. Mirrors routePermissions.ts's excludePermissions.
    //   hidden: !permissions.includes('CUSTOMER_MAPPER') || permissions.includes('ADMIN'),
    //   list: [
    //     {
    //       id: 1,
    //       items: [
    //         {
    //           label: t('customer.newCustomer'),
    //           to: routes.newCustomer,
    //         },
    //         {
    //           label: t('customer.myCustomers'),
    //           to: routes.myCustomers,
    //         },
    //       ],
    //     },
    //   ],
    // },
  ];
};

export { useNavData };
